import React, { useCallback, useState } from "react";
import Header from "./Header";
import "./ExportedEndOfYear.scss";
import {
  CvesPage,
  TopCommonIssuesPage,
  ResourcesPage,
  TagsPage,
  TotalAutomationsPage,
  TopAssigneesPage,
  TopQueuesPage,
  AccomplishmentsPage,
  IntegrationsPage,
  AggregationsPage,
  IntroPage,
  FinalPage,
} from "./Pages";
import useExportPDF from "../../Tools/useExportPDF";
import EndOfYearPdf from "./EndOfYearPdf";
import { domToPng } from "modern-screenshot";
import { slidesOrder } from "../EOYUtils";

const exportedPageKeyToComponent = {
  INTEGRATIONS_PAGE: IntegrationsPage,
  AGGREGATIONS_PAGE: AggregationsPage,
  CVES_PAGE: CvesPage,
  TOP_COMMON_ISSUES_PAGE: TopCommonIssuesPage,
  RESOURCES_PAGE: ResourcesPage,
  TAGS_PAGE: TagsPage,
  TOTAL_AUTOMATIONS_PAGE: TotalAutomationsPage,
  TOP_ASSIGNEES_PAGE: TopAssigneesPage,
  TOP_QUEUES_PAGE: TopQueuesPage,
  ACCOMPLISHMENTS_PAGE: AccomplishmentsPage,
};

interface ExportedEndOfYearProps {
  data: any;
  pages: string[];
  shouldDownload: boolean;
  onDownloadEnd: () => void;
}

const ExportedEndOfYear: React.FC<ExportedEndOfYearProps> = ({
  data,
  pages,
  shouldDownload,
  onDownloadEnd,
}) => {
  const [pagesRefsList, setPagesRefsList] = useState<
    Record<string, HTMLDivElement>
  >({});
  const updatePageRef = (page: string, ref: HTMLDivElement) =>
    setPagesRefsList((prevList) => ({ ...prevList, [page]: ref }));

  const getDataUrls = useCallback(async () => {
    const dataUrls = await Promise.all(
      Object.entries(pagesRefsList)
        ?.sort((a, b) => slidesOrder.indexOf(a[0]) - slidesOrder.indexOf(b[0]))
        .map(([_, ref]) =>
          domToPng(ref, { quality: 1, scale: 3, filter: (node) => true })
        )
    );
    return dataUrls;
  }, [pagesRefsList]);

  useExportPDF({
    shouldDownload: shouldDownload,
    pdfComponent: <EndOfYearPdf />,
    getDataUrls,
    filename: `Seemplicity Year Report 2025.pdf`,
    toastMessage: "Year Report 2025 Exported as PDF",
    onDownloadFinished: onDownloadEnd,
  });

  return (
    <div className={"exported-end-of-year"}>
      <Header addRef={(ref) => updatePageRef("HEADER", ref)} />
      <IntroPage addRef={(ref) => updatePageRef("INTRO_PAGE", ref)} />
      {pages.map((page, index) => {
        const PageComponent =
          exportedPageKeyToComponent[
            page as keyof typeof exportedPageKeyToComponent
          ];
        return !!PageComponent ? (
          <PageComponent
            key={page}
            data={data[page]}
            hasAssignees={!!pages.includes("TOP_ASSIGNEES_PAGE")}
            addRef={(ref) => updatePageRef(page, ref)}
            index={index + 1}
            {...(page === "CVES_PAGE"
              ? { hasAggregations: pages.includes("AGGREGATIONS_PAGE") }
              : {})}
            {...(page === "TAGS_PAGE"
              ? { hasResourcesPage: pages.includes("RESOURCES_PAGE") }
              : {})}
            {...(page === "RESOURCES_PAGE"
              ? { hasTagsPage: pages.includes("TAGS_PAGE") }
              : {})}
            {...(page === "TOP_ASSIGNEES_PAGE"
              ? { hasQueues: pages.includes("TOP_QUEUES_PAGE") }
              : {})}
          />
        ) : null;
      })}
      <FinalPage addRef={(ref) => updatePageRef("FINAL_PAGE", ref)} />
    </div>
  );
};

export default ExportedEndOfYear;
